import { ListingClient, PosClientConfig } from 'src/WebApiController';

/**
 * POS-9099 — chained "Add Retail Sale" workflow rollback decision.
 *
 * The chain creates placeholder seat-saver inventory, then opens the retail-sale create
 * modal seeded with exactly those listings. If the user closes the retail step WITHOUT
 * completing a sale, the just-created seat-savers should be deleted (rolled back) — they
 * were only created as a staging area for the sale.
 *
 * Pure so it can be unit-tested without React/network.
 */
export const shouldRollback = (
  saleCommitted: boolean,
  createdListingIds: number[]
): boolean => !saleCommitted && createdListingIds.length > 0;

/**
 * Best-effort deletion of the seat-savers created by the chain. Loops the existing single
 * `deleteSeatSaver` endpoint (N is small — one workflow's worth of new inventory).
 *
 * Note: `deleteSeatSaver` is itself guarded server-side (it only deletes a listing that is
 * still a seat-saver), so a listing that ended up sold/allocated is a safe no-op even if
 * this is called spuriously. Failures are swallowed (allSettled) — a failed rollback must
 * not block closing the modal.
 */
export const rollbackCreatedSeatSavers = async (
  config: PosClientConfig,
  createdListingIds: number[]
): Promise<void> => {
  const client = new ListingClient(config);
  await Promise.allSettled(
    createdListingIds.map((id) => client.deleteSeatSaver(id))
  );
};
